// components/TargetRow.tsx
import clsx from "clsx";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Badge, Label, Tooltip } from "@fronterahealth/frontera-ui-components";

import { getStatusAppearance, getStatusIcon } from "@components/TreatmentPlan/ProgrammingGoalCard/helper";
import { Target, useClientProgrammingData } from "@providers/ClientProgrammingProvider";

interface TargetRowProps {
  target: Target;
  tooltipText?: string;
  isFilteredtarget?: boolean;
  highlightText?: (text: string | null | undefined) => string | (string | JSX.Element)[] | null | undefined;
}

// Determine if this is an uncategorized target based on the target ID
const isUncategorizedTarget = (targetId: string) => targetId === "uncategorizedTarget";

export const TargetRow: React.FC<TargetRowProps> = ({
  target,
  tooltipText,
  isFilteredtarget = false,
  highlightText,
}) => {
  const navigate = useNavigate();
  const { clientId, goalId } = useParams();
  const { selectedTarget, setSelectedTargetId, searchGoalText, isTreatmentPlanUploadEnabled } =
    useClientProgrammingData();

  const isSelected = selectedTarget?.id === target.id;
  const Icon = target.status ? getStatusIcon(target.status) : React.Fragment;
  const isUncategorized = isUncategorizedTarget(target.id);

  const targetMomentCount = target.mediatargetpredictionSet?.totalCount || 0;

  const handleTargetClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedTargetId(target.id);

    const targetGoalId = isFilteredtarget ? target.goal?.id : target.goal?.id || goalId;

    if (!targetGoalId) {
      return;
    }
    if (searchGoalText) {
      navigate(`/clients/${clientId}/programming/goal/${targetGoalId}/target/${target.id}?search=${searchGoalText}`);
    } else {
      navigate(`/clients/${clientId}/programming/goal/${targetGoalId}/target/${target.id}`);
    }
  };

  return (
    <div
      className={`flex flex-col  bg-white justify-between items-center  gap-1 p-2 border border-limestone-100 rounded-lg cursor-pointer transition ${
        isSelected ? " border-2 bg-green-50 border-green-700!" : "hover:bg-green-50"
      }`}
      onClick={handleTargetClick}
    >
      <div className="flex items-center w-full gap-1">
        <Label
          size="extra-small"
          className={clsx("truncate w-[85%] ml-3", isUncategorized && "italic")}
          colorType="primary"
        >
          {highlightText ? highlightText(target.name) : target.name}
        </Label>
        <div className="flex justify-end flex-row gap-2 items-center w-full">
          {tooltipText ? (
            <Tooltip className="pointer-events-auto" text={tooltipText} infoIconClassName="h-4! w-4!" />
          ) : null}
          {target.status && !isUncategorized && (
            <Tooltip text={target.status} showInfoIcon={false}>
              <Badge
                text={
                  <div className="flex flex-row gap-1">
                    <Icon className="h-3 w-3" />
                  </div>
                }
                className="text-sm"
                appearance={getStatusAppearance(target.status)}
              />
            </Tooltip>
          )}
          {typeof targetMomentCount === "number" && isTreatmentPlanUploadEnabled && (
            <Tooltip text={`${targetMomentCount} Video Moments`} showInfoIcon={false} disabled={targetMomentCount < 99}>
              <Badge
                text={
                  <Label size="extra-small" displayType="loud">
                    {targetMomentCount > 99 ? "99+" : targetMomentCount}
                  </Label>
                }
                appearance="neutral"
                className="px-[6px]! py-1!"
              />
            </Tooltip>
          )}
        </div>
      </div>
      {isFilteredtarget ? (
        <div className="flex w-full  justify-start">
          <Label size="extra-small" className="ml-3">
            {target.goal?.name}
          </Label>
        </div>
      ) : null}
    </div>
  );
};
